import { Injectable } from '@angular/core';
import { isNil } from 'lodash';
import { DoenKidsGenericApiProvider } from 'src/providers/generic.provider';
import { IActivity, IOrganizationUnitOverview } from 'typings/doenkids/doenkids';
// eslint-disable-next-line max-len
import {
  IOrganizationUnitActivityAssignments,
  IRelatedActivityResponse,
  IActivityListResponse,
  ICountResponse,
  IActivityProgramTemplateUsageResponse,
  IActivityActivityExportBody,
  IActivityConnectionsListResponse,
  IActivityDetails,
  IActivityTypeListResponse,
  IAgeGroupListResponse,
  IGroupSizeListResponse,
  IActivityKindListResponse,
  IAreaOfDevelopmentListResponse,
  IRangeOfDevelopmentListResponse,
  ITagListResponse,
} from 'typings/api-activity';
import { IAttachmentListResponse } from 'typings/api-media';
import { IEventLogListResponse } from 'typings/api-customer';
import { firstValueFrom, Observable } from 'rxjs';
import { DoenkidsFileDownloader } from 'src/providers/download-files.provider';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { ActivityStore } from './activity.store';
import { ActivityQuery } from './activity.query';
import { I18nToastProvider } from '../../../providers/i18n-toast.provider';

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  constructor(
    private store: ActivityStore,
    private query: ActivityQuery,
    private $baseApi: DoenKidsGenericApiProvider,
    private $i18nToastProvider: I18nToastProvider,
    private $doenkidsFileDownloaderService: DoenkidsFileDownloader,
  ) {}

  async fetch(activityId: number) {
    try {
      const fetchedActivity = (await firstValueFrom(this.$baseApi.genericGetCall(`/activity/activity/${activityId}`))) as IActivityDetails;

      if (fetchedActivity) {
        this.store.upsert(fetchedActivity.data.id, {
          id: fetchedActivity.data.id,
          data: fetchedActivity.data,
          access_level: fetchedActivity.access_level,
          notifications_state: fetchedActivity.notifications_state,
          original: fetchedActivity.original,
        });
      }

      return fetchedActivity;
    } catch (e) {
      return this.$i18nToastProvider.error(_('activity.retrieve.failed'), { activityId });
    }
  }

  async fetchAll(
    organizationUnitId: number,
    statusId?: number,
    limit = 100,
    skip = 0,
    sortField = 'created_at',
    sortDirection = 'DESC',
    archived = false,
    nodeOnly = false,
  ) {
    try {
      const result = (await firstValueFrom(
        this.$baseApi.genericListCall('/activity/activity', limit || 100, skip || 0, sortField, sortDirection, {
          archived,
          nodeOnly,
          organizationUnitId,
          statusId,
        }),
      )) as IActivityListResponse;

      if (result) {
        return result;
      }
      return { items: [] };
    } catch (e) {
      throw e;
    }
  }

  async fetchApprovalCount(organizationUnitId: number) {
    try {
      const result = await firstValueFrom(
        this.$baseApi.genericListCall('/activity/activity/approval/count/', undefined, undefined, undefined, undefined, {
          organization_unit_id: organizationUnitId,
        }),
      );

      return result as ICountResponse;
    } catch (e) {
      return null;
    }

    return null;
  }

  fetchProgramTemplateUsage(activityId: number, organizationUnitId: number) {
    return this.$baseApi.genericGetCall(
      `/activity/activity/${activityId}/program-template-usage/${organizationUnitId}`,
    ) as Observable<IActivityProgramTemplateUsageResponse>;
  }

  /**
   * Creates an activity.
   *
   * `aiAdjustmentId` marks the activity as created from an accepted AI
   * proposal and closes that proposal's ledger row. The identifier is single
   * use: exactly one create or update may carry it, and a second attempt is
   * rejected. The `ai_generated` flag itself is server set.
   */
  create(organizationUnitId: number, activity: IActivity, aiAdjustmentId?: number) {
    return this.$baseApi.genericPostCall('/activity/activity', activity, null, {
      organizationUnitId,
      aiAdjustmentId,
    });
  }

  /**
   * Updates an activity.
   *
   * See `create` for `aiAdjustmentId`. On an update the API also matches the
   * ledger row against the body's `id`, so `activity.id` must be set.
   */
  async update(activityId: number, activity: IActivity, aiAdjustmentId?: number) {
    const updatedActivity = (await firstValueFrom(
      this.$baseApi.genericPutCall(`/activity/activity/${activityId}`, activity, isNil(aiAdjustmentId) ? undefined : { aiAdjustmentId }),
    )) as IActivity;
    const currentStoreEntity = await this.query.getEntity(updatedActivity.id);

    if (currentStoreEntity && updatedActivity) {
      this.store.update(updatedActivity.id, {
        ...currentStoreEntity,
        data: updatedActivity,
      });
    } else if (updatedActivity) {
      this.store.upsert(updatedActivity.id, {
        id: updatedActivity.id,
        data: updatedActivity,
      });
    }

    return updatedActivity;
  }

  async archive(activityId: number) {
    await firstValueFrom(this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}`));

    this.store.remove(activityId);
  }

  async dismissOriginalUpdatedNotification(activityId: number) {
    await firstValueFrom(this.$baseApi.genericPutCall(`/activity/activity/${activityId}/dismiss-original-updated-notification`, {}));
    await this.fetch(activityId);
  }

  async copy(
    activityId: number,
    organizationUnitId: number,
    optionalParams?: {
      statusId?: number;
      archived?: boolean;
      autoTranslate?: boolean;
    },
  ) {
    let url = `/activity/activity/${activityId}/organization-unit/${organizationUnitId}`;

    url = this.$baseApi.appendQueryParam(url, 'statusId', optionalParams?.statusId ?? 1);
    url = this.$baseApi.appendQueryParam(url, 'archived', optionalParams?.archived ?? false);
    if (optionalParams?.autoTranslate) {
      url = this.$baseApi.appendQueryParam(url, 'autoTranslate', optionalParams?.autoTranslate);
    }

    const copiedActivity = (await firstValueFrom(this.$baseApi.genericPutCall(url))) as IActivity;

    return copiedActivity;
  }

  approve(activityId: number, organizationUnitId: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/organization-unit/${organizationUnitId}/approved/${activityId}`);
  }

  unapprove(activityId: number, organizationUnitId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/organization-unit/${organizationUnitId}/approved/${activityId}`);
  }

  media(activityId: number) {
    return this.$baseApi.genericGetCall(`/activity/activity/${activityId}/media`);
  }

  organizationUnits(activityId: number) {
    return this.$baseApi.genericGetCall(`/activity/activity/${activityId}/organization-unit`);
  }

  requestReview(activityId: number, organizationUnitId: number, sourceOrganizationUnitId: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/organization-unit/${organizationUnitId}/review/${sourceOrganizationUnitId}`);
  }

  updateOrganizationUnits(activityId: number, roles: IOrganizationUnitActivityAssignments) {
    return this.$baseApi.genericPostCall(`/activity/activity/${activityId}/organization-unit`, roles);
  }

  activityKind(
    activityId: number,
    limit: number = 100,
    skip: number = 0,
    sortField: string = 'created_at',
    sortDirection: string = 'desc',
    search: string = '',
  ) {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/activity-kind`, limit, skip, sortField, sortDirection, { search });
  }

  addActivityKind(activityId: number, activityKindId: number, weight?: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/activity-kind/${activityKindId}`, undefined, { weight });
  }

  archiveActivityKind(activityId: number, activityKindId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/activity-kind/${activityKindId}`);
  }

  areaOfDevelopment(
    activityId: number,
    limit: number = 100,
    skip: number = 0,
    sortField: string = 'created_at',
    sortDirection: string = 'desc',
    search: string = '',
  ) {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/area-of-development`, limit, skip, sortField, sortDirection, { search });
  }

  addAreaOfDevelopment(activityId: number, areaOfDevelopmentId: number, weight?: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/area-of-development/${areaOfDevelopmentId}`, undefined, { weight });
  }

  archiveAreaOfDevelopment(activityId: number, areaOfDevelopmentId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/area-of-development/${areaOfDevelopmentId}`);
  }

  activityRangeOfDevelopment(
    activityId: number,
    limit: number = 100,
    skip: number = 0,
    sortField: string = 'created_at',
    sortDirection: string = 'desc',
    search: string = '',
  ) {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/range-of-development`, limit, skip, sortField, sortDirection, { search });
  }

  addRangeOfDevelopment(activityId: number, rangeOfDevelopmentId: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/range-of-development/${rangeOfDevelopmentId}`);
  }

  archiveRangeOfDevelopment(activityId: number, rangeOfDevelopmentId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/range-of-development/${rangeOfDevelopmentId}`);
  }

  ageGroup(activityId: number, limit: number = 100, skip: number = 0, sortField: string = 'created_at', sortDirection: string = 'desc', search: string = '') {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/age-group`, limit, skip, sortField, sortDirection, { search });
  }

  addAgeGroup(activityId: number, ageGroupId: number, weight?: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/age-group/${ageGroupId}`, undefined, { weight });
  }

  archiveAgeGroup(activityId: number, ageGroupId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/age-group/${ageGroupId}`);
  }

  activityLocation(
    activityId: number,
    limit: number = 100,
    skip: number = 0,
    sortField: string = 'created_at',
    sortDirection: string = 'desc',
    search: string = '',
  ) {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/location`, limit, skip, sortField, sortDirection, { search });
  }

  addActivityLocation(activityId: number, locationId: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/location/${locationId}`);
  }

  archiveActivityLocation(activityId: number, locationId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/location/${locationId}`);
  }

  activityTag(
    activityId: number,
    limit: number = 100,
    skip: number = 0,
    sortField: string = 'created_at',
    sortDirection: string = 'desc',
    search: string = '',
  ) {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/tag`, limit, skip, sortField, sortDirection, { search });
  }

  addActivityTag(activityId: number, tagId: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/tag/${tagId}`);
  }

  archiveActivityTag(activityId: number, tagId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/tag/${tagId}`);
  }

  attachment(activityId: number, limit: number = 100, skip: number = 0, sortField: string = 'created_at', sortDirection: string = 'desc') {
    return this.$baseApi.genericListCall(`/media/attachment/${activityId}`, limit, skip, sortField, sortDirection);
  }

  addAttachment(activityId: number, mediaId: number) {
    return this.$baseApi.genericPutCall(`/media/attachment/${activityId}/attachment/${mediaId}`);
  }

  archiveAttachment(activityId: number, mediaId: number) {
    return this.$baseApi.genericDeleteCall(`/media/attachment/${activityId}/attachment/${mediaId}`);
  }

  groupSize(activityId: number, limit: number = 100, skip: number = 0, sortField: string = 'created_at', sortDirection: string = 'desc', search: string = '') {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/group-size`, limit, skip, sortField, sortDirection, { search });
  }

  addGroupSize(activityId: number, groupSizeId: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/group-size/${groupSizeId}`);
  }

  archiveGroupSize(activityId: number, groupSizeId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/group-size/${groupSizeId}`);
  }

  activityType(
    activityId: number,
    limit: number = 100,
    skip: number = 0,
    sortField: string = 'created_at',
    sortDirection: string = 'desc',
    search: string = '',
  ) {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/activity-type`, limit, skip, sortField, sortDirection, { search });
  }

  addActivityType(activityId: number, activityTypeId: number, weight?: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/activity-type/${activityTypeId}`, undefined, {
      weight,
    });
  }

  archiveActivityType(activityId: number, activityTypeId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/activity-type/${activityTypeId}`);
  }

  kvsObservationPoints(activityId: number, limit: number = 100, skip: number = 0, sortField: string = 'code', sortDirection: string = 'desc') {
    return this.$baseApi.genericListCall(`/activity/activity/${activityId}/kvs-observation-point`, limit, skip, sortField, sortDirection);
  }

  addKvsObservationPoint(activityId: number, observationPointCode: string, weight: number) {
    return this.$baseApi.genericPutCall(`/activity/activity/${activityId}/kvs-observation-point/${observationPointCode}`, undefined, {
      weight,
    });
  }

  archiveKvsObservationPoint(activityId: number, observationPointCode: string) {
    return this.$baseApi.genericDeleteCall(`/activity/activity/${activityId}/kvs-observation-point/${observationPointCode}`);
  }

  getCopyAndSource(activityId: number) {
    return this.$baseApi.genericGetCall(`/activity/activity/${activityId}/related`) as Observable<IRelatedActivityResponse>;
  }

  eventLog(activityId: number, limit: number = 20, skip: number = 0) {
    return this.$baseApi.genericGetCall(`/activity/activity/${activityId}/event-log?limit=${limit}&skip=${skip}`) as Observable<IEventLogListResponse>;
  }

  activityExport(organizationUnitId: number, requestParams: IActivityActivityExportBody) {
    let url = `${this.$baseApi.API_END_POINT}/activity/activity/organization/${organizationUnitId}/export`;
    const headers = this.$baseApi.getAuthorizationHeader();

    const fullFileName = requestParams.filename.endsWith('.xlsx') ? requestParams.filename : `${requestParams.filename}.xlsx`;
    requestParams.filename = fullFileName;

    this.$doenkidsFileDownloaderService.addDownload({ name: fullFileName, url, headers, method: 'POST', requestBody: requestParams });
  }

  doenkidsActivityExport() {
    const url = `${this.$baseApi.API_END_POINT}/activity/activity/organization/doenkids-export`;
    const headers = this.$baseApi.getAuthorizationHeader();

    this.$doenkidsFileDownloaderService.addDownload({ name: 'doenkids-activiteit-export.xlsx', url, headers, method: 'GET' });
  }

  activityMediaUsageExport(organizationUnit: IOrganizationUnitOverview) {
    const url = `${this.$baseApi.API_END_POINT}/activity/activity/${organizationUnit.id}/media-usage/export`;
    const headers = this.$baseApi.getAuthorizationHeader();

    const ouName = organizationUnit.name ?? organizationUnit.id?.toString() ?? '';

    this.$doenkidsFileDownloaderService.addDownload({
      name: `${ouName.toLowerCase()}-activiteiten-met-media-gebruik-export.xlsx`,
      url,
      headers,
      method: 'GET',
    });
  }

  relatedActivities(activityId: number, limit: number = 100, skip: number = 0, sortField: string = 'created_at', sortDirection: string = 'desc') {
    return this.$baseApi.genericListCall(
      `/activity/related-activity/${activityId}`,
      limit,
      skip,
      sortField,
      sortDirection,
    ) as Observable<IActivityListResponse>;
  }

  /**
   * Copies every taxonomy link of one activity onto another: types,
   * attachments, age groups, group sizes, kinds, areas and ranges of
   * development, and tags.
   *
   * Shared by the manual copy action and the AI "save as copy" flow so the two
   * cannot drift in what a copy carries over. Section content is deliberately
   * NOT copied here: the manual copy duplicates the source sections while the
   * AI flow writes the proposed ones, so the caller owns that half.
   *
   * @param fromActivityId The activity to read the links from
   * @param toActivityId The activity to write the links to
   */
  async copyRelations(fromActivityId: number, toActivityId: number) {
    const [
      activityTypes,
      activityAttachments,
      activityAgeGroups,
      activityGroupSizes,
      activityActivityKinds,
      activityDevelopmentAreas,
      activityDevelopmentRanges,
      activityTags,
    ] = await Promise.all([
      firstValueFrom(this.activityType(fromActivityId)) as Promise<IActivityTypeListResponse>,
      firstValueFrom(this.attachment(fromActivityId)) as Promise<IAttachmentListResponse>,
      firstValueFrom(this.ageGroup(fromActivityId)) as Promise<IAgeGroupListResponse>,
      firstValueFrom(this.groupSize(fromActivityId)) as Promise<IGroupSizeListResponse>,
      firstValueFrom(this.activityKind(fromActivityId)) as Promise<IActivityKindListResponse>,
      firstValueFrom(this.areaOfDevelopment(fromActivityId)) as Promise<IAreaOfDevelopmentListResponse>,
      firstValueFrom(this.activityRangeOfDevelopment(fromActivityId)) as Promise<IRangeOfDevelopmentListResponse>,
      firstValueFrom(this.activityTag(fromActivityId)) as Promise<ITagListResponse>,
    ]);

    const promises: Promise<any>[] = [];

    for (const activityType of activityTypes?.items ?? []) {
      promises.push(firstValueFrom(this.addActivityType(toActivityId, activityType.id as number)));
    }
    for (const activityAttachment of activityAttachments?.items ?? []) {
      promises.push(firstValueFrom(this.addAttachment(toActivityId, activityAttachment.media_id)));
    }
    for (const activityAgeGroup of activityAgeGroups?.items ?? []) {
      promises.push(firstValueFrom(this.addAgeGroup(toActivityId, activityAgeGroup.id as number, activityAgeGroup.weight)));
    }
    for (const activityGroupSize of activityGroupSizes?.items ?? []) {
      promises.push(firstValueFrom(this.addGroupSize(toActivityId, activityGroupSize.id as number)));
    }
    for (const activityActivityKind of activityActivityKinds?.items ?? []) {
      promises.push(firstValueFrom(this.addActivityKind(toActivityId, activityActivityKind.id as number, activityActivityKind.weight)));
    }
    for (const activityDevelopmentArea of activityDevelopmentAreas?.items ?? []) {
      promises.push(firstValueFrom(this.addAreaOfDevelopment(toActivityId, activityDevelopmentArea.id as number, activityDevelopmentArea.weight)));
    }
    for (const activityDevelopmentRange of activityDevelopmentRanges?.items ?? []) {
      promises.push(firstValueFrom(this.addRangeOfDevelopment(toActivityId, activityDevelopmentRange.id as number)));
    }
    for (const activityTag of activityTags?.items ?? []) {
      promises.push(firstValueFrom(this.addActivityTag(toActivityId, activityTag.id as number)));
    }

    await Promise.all(promises);
  }

  addRelatedActivity(fromActivityId: number, toActivityId: number) {
    return this.$baseApi.genericPutCall(`/activity/related-activity/${fromActivityId}/activity/${toActivityId}`);
  }

  removeRelatedActivity(fromActivityId: number, toActivityId: number) {
    return this.$baseApi.genericDeleteCall(`/activity/related-activity/${fromActivityId}/activity/${toActivityId}`);
  }

  listConnections(activityId: number, organizationUnitId?: number) {
    let url = `/activity/activity/list-connections/${activityId}`;
    url = this.$baseApi.appendQueryParam(url, 'organization_unit_id', organizationUnitId);

    return this.$baseApi.genericPostCall(url, {}) as Observable<IActivityConnectionsListResponse>;
  }

  async recopy(
    activityId: number,
    organizationUnitId: number,
    optionalParams?: {
      statusId?: number;
      archived?: boolean;
      dryRun?: boolean;
    },
  ) {
    let url = `/activity/activity/${activityId}/organization-unit/${organizationUnitId}/recopy`;

    if (optionalParams?.statusId) {
      url = this.$baseApi.appendQueryParam(url, 'statusId', optionalParams.statusId);
    }
    if (optionalParams?.archived !== undefined) {
      url = this.$baseApi.appendQueryParam(url, 'archived', optionalParams.archived);
    }
    if (optionalParams?.dryRun !== undefined) {
      url = this.$baseApi.appendQueryParam(url, 'dryRun', optionalParams.dryRun);
    }

    const response = await firstValueFrom(this.$baseApi.genericPutCall(url));

    return response;
  }
}
